// Filename-based routing for uploaded documents.
//
// The uploader never files something silently: it proposes a destination with
// the evidence it used, and the owner confirms or overrides it before the
// upload commits.

import type {
  BusinessEntity,
  DocumentCategory,
  DocumentFolder,
  FinancialAccount,
} from '../types/family-finance'

export interface RouteSuggestion {
  folder: DocumentFolder
  category: DocumentCategory
  taxYear: number | null
  entityId: string | null
  accountId: string | null
  source: string
  /** Human-readable evidence, e.g. "matched “1099-NEC” · tax year 2025". */
  reason: string
  confidence: 'high' | 'medium' | 'low'
}

export interface RouteContext {
  entities: BusinessEntity[]
  accounts: FinancialAccount[]
}

// ─── Text helpers ────────────────────────────────────────────

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/\.[a-z0-9]{1,5}$/, '') // drop the extension
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** Word-boundary containment: "1099 nec" matches "obfh 1099 nec 2025". */
function has(haystack: string, needle: string): boolean {
  const n = normalize(needle)
  return n.length > 0 && ` ${haystack} `.includes(` ${n} `)
}

function hasAny(haystack: string, needles: string[]): string | null {
  return needles.find((n) => has(haystack, n)) ?? null
}

// Tokens that carry no identifying signal when matching an entity name.
const STOPWORDS = new Set([
  'llc', 'inc', 'pllc', 'lp', 'llp', 'corp', 'co', 'the', 'and', 'of',
  'holdings', 'holding', 'company', 'solutions', 'services', 'group',
  'management', 'partners', 'financial', 'properties', 'property',
])

// ─── Signal tables ───────────────────────────────────────────
// Ordered most-specific first: "1099 nec" must beat a bare "1099".

const TAX_SIGNALS: Array<[DocumentCategory, string[]]> = [
  ['W-2', ['w 2', 'w2', 'wage and tax statement']],
  ['1099-NEC', ['1099 nec', '1099nec', 'nonemployee compensation']],
  ['1099-INT', ['1099 int', '1099int', 'interest income']],
  ['1099-DIV', ['1099 div', '1099div', 'dividends and distributions']],
  ['1099-B', ['1099 b', '1099b', 'proceeds from broker']],
  ['1099-K', ['1099 k', '1099k', 'payment card']],
  ['1099-MISC', ['1099 misc', '1099misc']],
  ['K-1', ['k 1', 'k1', 'schedule k 1', 'form 1065 k 1']],
  ['1098', ['1098', 'mortgage interest statement']],
  ['5498', ['5498', 'ira contribution information']],
  ['Prior Return', ['1040', '1040x', 'tax return', 'filed return', 'prior return', '1120', '1120s', '1065']],
  ['Receipt', ['receipt', 'property tax', 'donation', 'charitable']],
  ['Other', ['1099', 'w 9', 'w9', 'tax', 'irs', 'estimated payment', '1040 es']],
]

const BUSINESS_SIGNALS: Array<[DocumentCategory, string[]]> = [
  ['Formation', ['certificate of formation', 'articles of organization', 'articles of incorporation', 'formation', 'certificate of filing']],
  ['Operating Agreement', ['operating agreement', 'company agreement', 'bylaws', 'shareholder agreement']],
  ['EIN Letter', ['ein', 'cp 575', 'cp575', 'employer identification']],
  ['Franchise Tax / PIR', ['franchise tax', 'public information report', 'pir', 'annual report']],
  ['Minutes / Resolution', ['minutes', 'resolution', 'consent', 'board']],
  ['Payroll', ['payroll', 'w 3', '941', '940', 'quarterly wage']],
  ['Ledger / P&L', ['profit and loss', 'p l', 'pnl', 'income statement', 'balance sheet', 'general ledger', 'ledger', 'trial balance']],
  ['Invoice', ['invoice', 'bill to', 'statement of account']],
  ['Contract', ['contract', 'agreement', 'engagement letter', 'msa', 'sow', 'nda', 'lease']],
  ['License / Permit', ['license', 'permit', 'registration', 'assumed name', 'dba']],
  ['Insurance', ['policy', 'certificate of insurance', 'coi', 'e o', 'malpractice']],
]

const ACCOUNT_SIGNALS: Array<[DocumentCategory, string[]]> = [
  ['Statement', ['statement', 'stmt', 'estatement', 'monthly statement', 'account summary']],
  ['Trade Confirmation', ['trade confirmation', 'confirm', 'confirmation', 'transaction advice']],
  ['Closing Statement', ['closing disclosure', 'settlement statement', 'hud 1', 'closing statement', 'alta']],
  ['Loan Document', ['promissory note', 'loan', 'note', 'amortization', 'payoff', 'deed of trust', 'mortgage']],
  ['Beneficiary Form', ['beneficiary', 'tod', 'pod', 'transfer on death']],
  ['Insurance', ['insurance', 'policy']],
]

// ─── Matchers ────────────────────────────────────────────────

function matchTaxYear(text: string): number | null {
  const thisYear = new Date().getFullYear()
  // Prefer an explicit "TY2025" / "tax year 2025" marker over a stray year.
  const tagged = text.match(/\b(?:ty|tax year|fy)\s?(20\d{2})\b/)
  if (tagged) return parseInt(tagged[1], 10)
  const years = [...text.matchAll(/\b(20\d{2})\b/g)]
    .map((m) => parseInt(m[1], 10))
    .filter((y) => y >= 2000 && y <= thisYear + 1)
  if (years.length === 0) return null
  // Multiple years in one name ("2025 return prepared 2026") — the earliest is
  // almost always the tax year; the later one is the prep/upload date.
  return Math.min(...years)
}

function matchEntity(text: string, entities: BusinessEntity[]): BusinessEntity | null {
  for (const entity of entities) {
    if (has(text, entity.shortName) || has(text, entity.name)) return entity
  }
  // Fall back to a distinctive word from the legal name ("lawdiem", "irpm").
  for (const entity of entities) {
    const tokens = normalize(entity.name)
      .split(' ')
      .filter((t) => t.length >= 4 && !STOPWORDS.has(t))
    if (tokens.some((t) => has(text, t))) return entity
  }
  return null
}

function matchAccount(text: string, accounts: FinancialAccount[]): FinancialAccount | null {
  // A 4-digit mask is the strongest signal in a bank filename.
  for (const account of accounts) {
    if (/^\d{4}$/.test(account.mask) && has(text, account.mask)) return account
  }
  for (const account of accounts) {
    if (has(text, account.name)) return account
  }
  // Institution-level match: pick that institution's only account, or none.
  for (const account of accounts) {
    if (!has(text, account.institutionName)) continue
    const siblings = accounts.filter((a) => a.institutionId === account.institutionId)
    return siblings.length === 1 ? account : null
  }
  return null
}

function firstSignal(
  text: string,
  table: Array<[DocumentCategory, string[]]>,
): { category: DocumentCategory; matched: string } | null {
  for (const [category, needles] of table) {
    const matched = hasAny(text, needles)
    if (matched) return { category, matched }
  }
  return null
}

// ─── Entry point ─────────────────────────────────────────────

export function routeDocument(fileName: string, ctx: RouteContext): RouteSuggestion {
  const text = normalize(fileName)
  const taxYear = matchTaxYear(text)
  const entity = matchEntity(text, ctx.entities)
  const account = matchAccount(text, ctx.accounts)

  const tax = firstSignal(text, TAX_SIGNALS)
  const business = firstSignal(text, BUSINESS_SIGNALS)
  const bank = firstSignal(text, ACCOUNT_SIGNALS)

  const evidence: string[] = []
  const push = (label: string) => evidence.push(label)

  // A tax form beats everything: a K-1 from OBFH is still a tax document, it
  // just carries the entity along with it.
  if (tax && tax.category !== 'Other') {
    push(`“${tax.matched}”`)
    if (taxYear) push(`tax year ${taxYear}`)
    if (entity) push(entity.shortName)
    return {
      folder: 'taxes',
      category: tax.category,
      taxYear,
      entityId: entity?.id ?? null,
      accountId: account?.id ?? null,
      source: entity?.shortName || account?.institutionName || '',
      reason: `matched ${evidence.join(' · ')}`,
      confidence: taxYear ? 'high' : 'medium',
    }
  }

  // Entity paperwork: an entity name plus a business-doc term, or either one
  // strongly on its own.
  if (entity && (business || !bank)) {
    push(entity.shortName)
    if (business) push(`“${business.matched}”`)
    if (taxYear) push(String(taxYear))
    return {
      folder: 'businesses',
      category: business?.category ?? 'Other',
      taxYear,
      entityId: entity.id,
      accountId: null,
      source: entity.name,
      reason: `matched ${evidence.join(' · ')}`,
      confidence: business ? 'high' : 'medium',
    }
  }
  if (business && !account) {
    push(`“${business.matched}”`)
    return {
      folder: 'businesses',
      category: business.category,
      taxYear,
      entityId: null,
      accountId: null,
      source: '',
      reason: `matched ${evidence.join(' · ')} — pick a business`,
      confidence: 'low',
    }
  }

  // Bank/brokerage paperwork.
  if (account || bank) {
    if (account) push(`${account.institutionName} ·· ${account.mask}`)
    if (bank) push(`“${bank.matched}”`)
    return {
      folder: 'accounts',
      category: bank?.category ?? 'Statement',
      taxYear,
      entityId: null,
      accountId: account?.id ?? null,
      source: account?.institutionName ?? '',
      reason: `matched ${evidence.join(' · ')}`,
      confidence: account && bank ? 'high' : 'medium',
    }
  }

  // Nothing but a year, or a weakly tax-ish word.
  if (tax || taxYear) {
    if (tax) push(`“${tax.matched}”`)
    if (taxYear) push(`tax year ${taxYear}`)
    return {
      folder: 'taxes',
      category: tax?.category ?? 'Other',
      taxYear,
      entityId: null,
      accountId: null,
      source: '',
      reason: `matched ${evidence.join(' · ')} — confirm the type`,
      confidence: 'low',
    }
  }

  return {
    folder: 'taxes',
    category: 'Other',
    taxYear: null,
    entityId: null,
    accountId: null,
    source: '',
    reason: 'no filename match — choose a destination',
    confidence: 'low',
  }
}

/** Strip the extension and tidy separators for the editable display name. */
export function suggestDisplayName(fileName: string): string {
  const base = fileName.replace(/\.[A-Za-z0-9]{1,5}$/, '')
  return base.replace(/[_]+/g, ' ').replace(/\s{2,}/g, ' ').trim() || fileName
}
